import React from "react";
import { acceptedMediaExtensions, FontFam, tailwind, Theme, themeSchema, User } from "../../../models/models";
import HttpService from "../../../services/http.service";
import { ModalContext } from "../../../components/modal/modal";
import { StorageContext } from "../../../components/storage/storage-context";

import MediaPicker from "../../../components/quick-form/media-picker";
import FileBrowserInput from "../../utilities/quick-form/custom-inputs/file-browser-input";
// import QuickForm, { QuickFormSchemaMetaType } from "../../../components/quick-form/quick-form";
import QuickForm, { QuickFormSchemaMetaType } from "../../../components/utilities/quick-form/quick-form";
import ValidationService, { COMMON_REGEXES, Schema } from "../../../services/validation.service";
import AspectContainer from "../../../components/aspect-container/aspect-container";

import Gallery2 from "../../../components/gallery/gallery2";
import FontService from "../../../services/font.service";
import ThemeCssModal from "./theme-css-modal";
import ThemeFontModal from "./theme-font-modal";
import Icons from "../../icons/icons";
import ColumnSnapper from "../../utilities/column-snapper/column-snapper";

const defaultAcceptedMediaExtensions: { [key: string]: string[] } = {
  image: ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
  video: ['.mp4', '.webm', '.ogg']
};

type ThemeForm = Omit<Theme, "id">;

const colors = {
  red: "bg-gradient-to-br from-red-200 via-red-300 to-red-300",
  orange: "bg-gradient-to-br from-orange-200 via-orange-300 to-orange-300",
  yellow: "bg-gradient-to-br from-yellow-200 via-yellow-300 to-yellow-300",
  green: "bg-gradient-to-br from-green-200 via-green-300 to-green-300",
  blue: "bg-gradient-to-br from-blue-200 via-blue-300 to-blue-300",
  pink: "bg-gradient-to-br from-pink-200 via-pink-300 to-pink-200",
  purple: "bg-gradient-to-br from-indigo-200 via-purple-300 to-indigo-200",
  gold: "bg-gradient-to-br from-gold-200 via-gold-300 to-gold-300",
  white: "bg-gradient-to-br from-slate-50 via-slate-200 to-slate-100",
  black: "bg-gradient-to-br from-slate-700 via-slate-900 to-slate-700"
}

const themeFormSchema: Schema<QuickFormSchemaMetaType> = {
  herovideo: { type: 'string', attributes: { required: true, strLength: { minLength: 1, maxLength: 256 }},
    meta: {
      quickForm: {
        CustomInput: FileBrowserInput,
        customInputProps: { 
          acceptedMediaExtensions: {   video: ['.mp4', '.webm', '.ogg'] }
        }
      }
    }
  },
  heroimage: { type: 'string', attributes: { required: true, strLength: { minLength: 1, maxLength: 256 }}, 
    meta: {
      quickForm: {
        CustomInput: FileBrowserInput,
        customInputProps: {
          acceptedMediaExtensions: { image: ['.jpg', '.jpeg', '.png', '.gif', '.webp'], }
        }
      }
    }
  },
  herotext: { type: 'string', attributes: { required: true, strLength: { maxLength: 2048 }},
    meta: {
      quickForm: {
        textArea: true, textAreaRows: 4,
        // placeholder: 'Write your hero text here...'
      }
    }
  },
  threecard1title: { type: 'string', attributes: { required: true, strLength: { minLength: 1, maxLength: 256 }}},
  threecard1image: { type: 'string', attributes: { required: true, strLength: { minLength: 1, maxLength: 256 }},
    meta: {
      quickForm: {
        CustomInput: FileBrowserInput,
        customInputProps: {
          acceptedMediaExtensions: { image: ['.jpg', '.jpeg', '.png', '.gif', '.webp'], }
        }
      }
    }
  },
  threecard1text: { type: 'string', attributes: { required: true, strLength: { maxLength: 2048 }},
    meta: {
      quickForm: {
        textArea: true, textAreaRows: 4,
        placeholder: 'Write your description here...'
      }
    }
  },
  threecard2title: { type: 'string', attributes: { required: true, strLength: { minLength: 1, maxLength: 256 }}},
  threecard2image: { type: 'string', attributes: { required: true, strLength: { minLength: 1, maxLength: 256 }},
    meta: {
      quickForm: {
        CustomInput: FileBrowserInput,
        customInputProps: {
          acceptedMediaExtensions: { image: ['.jpg', '.jpeg', '.png', '.gif', '.webp'], }
        }
      }
    }
  },
  threecard2text: { type: 'string', attributes: { required: true, strLength: { maxLength: 2048 }}, 
    meta: {
      quickForm: {
        textArea: true, textAreaRows: 4,
        placeholder: 'Write your description here...'
      }
    }
  },
  threecard3title: { type: 'string', attributes: { required: true, strLength: { minLength: 1, maxLength: 256 }}},
  threecard3image: { type: 'string', attributes: { required: true, strLength: { minLength: 1, maxLength: 256 }},
    meta: {
      quickForm: {
        CustomInput: FileBrowserInput,
        customInputProps: {
          acceptedMediaExtensions: { image: ['.jpg', '.jpeg', '.png', '.gif', '.webp'], }
        }
      }
    }
  },
  threecard3text: { type: 'string', attributes: { required: true, strLength: { maxLength: 2048 }},
    meta: {
      quickForm: {
        textArea: true, textAreaRows: 4,
        placeholder: 'Write your description here...'
      }
    }
  },
  about: { type: 'string', attributes: { required: true, strLength: { maxLength: 4096 }},
    meta: {
      quickForm: {
        textArea: true, textAreaRows: 4,
        placeholder: 'Write your description here...'
      }
    }
  },
  card1color: { type: [...Object.keys(colors)], attributes: { required: true, strLength: { minLength: 1, maxLength: 256 }}},
  card2color: { type: [...Object.keys(colors)], attributes: { required: true, strLength: { minLength: 1, maxLength: 256 }}},
  font: { type: 'string', attributes: { required: true, strLength: { minLength: 1, maxLength: 256 }}},
}

const heroPartitionedFormSchema = (({ herovideo, heroimage, herotext }) => ({ herovideo, heroimage, herotext }))(themeFormSchema);

const AdminThemeForm: React.FC<{ initTheme: Theme }> = ({ initTheme }) => {

  const modalContext = React.useContext(ModalContext);
  const [storageContext, setStorageContext] = React.useContext(StorageContext);
  const [model, setModel] = React.useState<ThemeForm>(initTheme);
  const [formErrors, setFormErrors] = React.useState<{ key: string, message: string }[]>([]);
  const [touched, setTouched] = React.useState<boolean>(false);

  // React.useEffect(() => {
  //   console.log('model updated', model);
  // }, [model]);
  
  const inputHandler = (err: string[], keyvalues: Partial<ThemeForm>) => {

    // prepare new keyvalues and previous keyvalues for comparison
    let prevValueSorted = Object.keys(keyvalues).sort().reduce((accumulator, key) => ({ ...accumulator, [key]: model[key as keyof typeof model]}), {});
    let newValueSorted = Object.keys(keyvalues).sort().reduce((accumulator, key) => ({ ...accumulator, [key]: keyvalues[key as keyof typeof keyvalues]}), {});

    // updating state without changes triggers infinite loop
    if (JSON.stringify(prevValueSorted) !== JSON.stringify(newValueSorted)) {

      setModel(modl => ({ ...modl, ...keyvalues }));

      setFormErrors(fe => {
        Object.keys(keyvalues).forEach(k => {
          fe = fe.filter(fff => fff.key !== k)
        });
        err.forEach(e => {
          const key = e.split(' ')[0];
          fe = [ ...fe.filter(fff => fff.key !== key), { key: key, message: e }]
        })
        return fe;
      });

      setTouched(true);

    }

  };

  // React.useEffect(() => {
  //   // load the theme font
  //   console.log('initTheme fonts', initTheme.fonts);
  // }, []);


  const [demoFonts, setDemoFonts] = React.useState<{ primary: FontFam, secondary: FontFam, tertiary: FontFam}>({
    primary: initTheme.fonts.primary,
    secondary: initTheme.fonts.secondary,
    tertiary: initTheme.fonts.tertiary
  })
  
  React.useEffect(() => {
    FontService.loadFont(model.fonts.primary, 'font-primary-demo');
    FontService.loadFont(model.fonts.secondary, 'font-secondary-demo');
    FontService.loadFont(model.fonts.tertiary, 'font-tertiary-demo');
  }, []);

  React.useEffect(() => {
    FontService.loadFont(demoFonts.primary, 'font-primary-demo');
    FontService.loadFont(demoFonts.secondary, 'font-secondary-demo');
    FontService.loadFont(demoFonts.tertiary, 'font-tertiary-demo');
  }, [demoFonts]);

  // React.useEffect(() => {
  //   onChange(model);
  // }, [model]);

  const cssModal = async (key1: "cards" | "forms" | "buttons", key2: "primary" | "secondary" | "tertiary") => {
    try {
      const modalRes = await (new Promise<{ classList: string } | null>((res, rej) => modalContext.modal!({
        node: (
          <ThemeCssModal 
            init={(model[key1 as keyof ThemeForm] as any)[key2]}
            cssObject={
              {
                cards: { ...(({
                  text_color,
                  backdrop_blur, 
                  background_color,
                  background_opacity,
                  border_radius,
                  border_style,
                  box_shadow,
                  border_width,
                  border_color
                }) => ({
                  text_color,
                  backdrop_blur, 
                  background_color,
                  background_opacity,
                  border_style,
                  border_radius,
                  box_shadow,
                  border_width,
                  border_color
                }))(tailwind) },
                forms: { ...tailwind },
                buttons: { ...tailwind },
              }[key1]
            }
            resolve={res}
          />
        ),
        resolve: res , reject: rej
      })));
      modalContext.modal!();
      if (modalRes?.classList) { 
        setModel(m => ({ ...m, [key1]: { ...(m[key1 as keyof ThemeForm] as any), [key2]: modalRes.classList }}));
        setTouched(true);
      }
    } catch {}
  }

  const fontModal = async (key: "primary" | "secondary" | "tertiary" ) => {
    
    try {
      const mediaRes = await (new Promise<{ fontFam: FontFam } | null>((res, rej) => modalContext.modal!({
        node: (<ThemeFontModal init={(model.fonts[key])} resolve={res} />),
        resolve: res, reject: rej
      })));
      modalContext.modal!();
      if (mediaRes?.fontFam?.src) {
        FontService.loadFont({ src: mediaRes.fontFam.src }, `font-${key}-demo` );
        setDemoFonts(df => ({ ...demoFonts, [key]: { src: mediaRes.fontFam.src }}));
        setModel(m => ({ ...m, fonts: { ...m.fonts, [key]: { src: mediaRes.fontFam.src }}}));
        setTouched(true);
      }
      if (mediaRes?.fontFam?.family) {
        FontService.loadFont({ family: mediaRes.fontFam.family }, `font-${key}-demo`);
        setDemoFonts(df => ({ ...demoFonts, [key]: { family: mediaRes.fontFam.family }}));
        setModel(m => ({ ...m, fonts: { ...m.fonts, [key]: { family: mediaRes.fontFam.family }}}));
        setTouched(true);
      }
    } catch {}
  }






  
  return (
    <div className="py-16 px-2 md:px-4 mx-auto">

      <div>

        {/* hero section */}
        <div className={`text-center mx-2 md:mx-8 px-2 md:px-8 py-8 my-8 ${  `bg-gradient-to-br from-pink-200 via-pink-300 to-pink-200` }`}>
          <Gallery2>
            <QuickForm<Pick<ThemeForm, "herovideo">>
              schema={{ herovideo: themeFormSchema.herovideo }}
              init={{ herovideo: model.herovideo }}
              onInput={inputHandler}
            />
            <QuickForm<Pick<ThemeForm, "heroimage">>
              schema={{ heroimage: themeFormSchema.heroimage }}
              init={{ heroimage: model.heroimage }}
              onInput={(err, hi) => inputHandler(err, hi)}
            />
            <QuickForm<{ herotext: string }>
              schema={{herotext: themeFormSchema.herotext}}
              init={{herotext: model.herotext}}
              onInput={inputHandler}
            />
          </Gallery2>
        </div>


        {/* three cards section */}
        <div className="text-center">
          <div className="inline-flex flex-col md:flex-row flex-wrap justify-center">
            
            <div className=" bg-slate-800 flex flex-col md:max-w-[20rem] p-4 m-4 border border-slate-600">
              <QuickForm<Pick<ThemeForm, "threecard1image" | "threecard1title" | "threecard1text">>
                schema={(({ threecard1image, threecard1title, threecard1text }) => ({ threecard1image, threecard1title, threecard1text }))(themeFormSchema)}
                init={(({ threecard1image, threecard1title, threecard1text }) => ({ threecard1image, threecard1title, threecard1text }))(model)}
                onInput={inputHandler}
              />
            </div>

            <div className=" bg-slate-800 flex flex-col md:max-w-[20rem] p-4 m-4 border border-slate-600">
              <QuickForm<Pick<ThemeForm, "threecard2image" | "threecard2title" | "threecard2text">>
                schema={(({ threecard2image, threecard2title, threecard2text }) => ({ threecard2image, threecard2title, threecard2text }))(themeFormSchema)}
                init={(({ threecard2image, threecard2title, threecard2text }) => ({ threecard2image, threecard2title, threecard2text }))(model)}
                onInput={inputHandler}
              />
            </div>

            <div className=" bg-slate-800 flex flex-col md:max-w-[20rem] p-4 m-4 border border-slate-600">
              <QuickForm<Pick<ThemeForm, "threecard3image" | "threecard3title" | "threecard3text">>
                schema={(({ threecard3image, threecard3title, threecard3text }) => ({ threecard3image, threecard3title, threecard3text }))(themeFormSchema)}
                init={(({ threecard3image, threecard3title, threecard3text }) => ({ threecard3image, threecard3title, threecard3text }))(model)}
                onInput={inputHandler}
              />
            </div>

          </div>
        </div>

        {/* about */}
        <div>
          <QuickForm<Pick<ThemeForm, "about">>
            schema={{ about: themeFormSchema.about }}
            init={{ about: model.about }}
            onInput={inputHandler}
          />
        </div>

        {/* colors */}
        {/* <div className={`text-center mx-2 md:mx-8 px-2 md:px-8 py-8 my-8 ${ model.card1color }`}>
          <QuickForm<Pick<ThemeForm, "card1color">>
            schema={{ card1color: themeFormSchema.card1color }}
            init={{ card1color: Object.keys(colors).find(k => colors[k as keyof typeof colors] === model.card1color )! }}
            onInput={(err, c1c) => inputHandler(err, { card1color: colors[c1c.card1color as keyof typeof colors] }) }
          />
        </div>

        <div className={`text-center mx-2 md:mx-8 px-2 md:px-8 py-8 my-8 ${ model.card2color }`}>

          <QuickForm<Pick<ThemeForm, "card2color">>
            schema={{ card2color: themeFormSchema.card2color }}
            init={{ card2color: Object.keys(colors).find(k => colors[k as keyof typeof colors] === model.card2color )! }}
            onInput={(err, c2c) => inputHandler(err, { card2color: colors[c2c.card2color as keyof typeof colors] }) }
          />

        </div> */}




      <h2 className={`font-black neon-text text-left block text-purple-400 text-3xl pt-4`}>Cards</h2>
      {/* <div className={`h-4 bg-slate-400 my-4 w-full`}></div> */}
      <ColumnSnapper wideColumns={3} narrowColumns={1} minColumnPixWidth={600}>
        {
          ["primary", "secondary", "tertiary"].map(k => (
            <div 
              key={k} 
              className={`${(model.cards as any)[k]} aspect-1 m-4 p-4 flex items-center justify-center relative cursor-pointer`}
              onClick={async (e) => { e.stopPropagation(); cssModal('cards', k as any)}}
            >
              <div 
                className="absolute top-0 right-0 w-14 h-14 bg-orange-400 flex items-center justify-center rounded-full translate-x-1/2 -translate-y-1/2 cursor-pointer"
                onClick={async (e) => { e.stopPropagation(); cssModal('cards', k as any); }}
              >
                <div className="p-3 text-black">{Icons.pencil}</div>
              </div>
              {k.charAt(0).toUpperCase() + k.slice(1)}
            </div>
          ))
        }
      </ColumnSnapper>

      <h2 className={`font-black neon-text text-left block text-teal-400 text-3xl pt-4`}>Inputs</h2>
      {/* <div className={`h-4 bg-slate-400 my-4 w-full`}></div> */}
      <ColumnSnapper wideColumns={3} narrowColumns={1} minColumnPixWidth={600}>
        {
          ["primary", "secondary", "tertiary"].map(k => (
            <div 
              key={k} 
              className={`aspect-1 m-4 p-4 flex items-center justify-center border-2 relative cursor-pointer`}
              onClick={async (e) => { e.stopPropagation(); cssModal('forms', k as any)}}
            >
              <div 
                className="absolute top-0 right-0 w-14 h-14 bg-orange-400 flex items-center justify-center rounded-full translate-x-1/2 -translate-y-1/2 cursor-pointer"
                onClick={async (e) => { e.stopPropagation(); cssModal('forms', k as any); }}
              >
                <div className="p-3 text-black">{Icons.pencil}</div>
              </div>
              <input
                placeholder={k.charAt(0).toUpperCase() + k.slice(1)}
                value={k.charAt(0).toUpperCase() + k.slice(1)}
                className={`${(model.forms as any)[k]} m-2 p-2 h-12 w-10/12`}
              >
              </input>
            </div>
          ))
        }
      </ColumnSnapper>

      <h2 className={`font-black neon-text text-left block text-orange-400 text-3xl pt-4`}>Buttons</h2>
      {/* <div className={`h-4 bg-slate-400 my-4 w-full`}></div> */}
      <ColumnSnapper wideColumns={3} narrowColumns={1} minColumnPixWidth={600}>
        {
          ["primary", "secondary", "tertiary"].map(k => (
            <div 
              key={k} 
              className={`aspect-1 m-4 p-4 flex items-center justify-center border-2 relative cursor-pointer`}
              onClick={async (e) => { e.stopPropagation(); cssModal('buttons', k as any)}}
            >
              <div 
                className="absolute top-0 right-0 w-14 h-14 bg-orange-400 flex items-center justify-center rounded-full translate-x-1/2 -translate-y-1/2 cursor-pointer"
                onClick={async (e) => { e.stopPropagation(); cssModal('buttons', k as any); }}
              >
                <div className="p-3 text-black">{Icons.pencil}</div>
              </div>
              <button
                className={`${(model.buttons as any)[k]} btn w-10/12`}
              >
                {k.charAt(0).toUpperCase() + k.slice(1)}
              </button>
            </div>
          ))
        }
      </ColumnSnapper>

      <h2 className={`font-black neon-text text-left block text-purple-400 text-3xl pt-4`}>Fonts</h2>
      {/* <div className={`h-4 bg-slate-400 my-4 w-full`}></div> */}
      <ColumnSnapper wideColumns={3} narrowColumns={1} minColumnPixWidth={600}>
        {
          ["primary", "secondary", "tertiary"].map(k => (
            <div 
              key={k} 
              className={`aspect-1 m-4 p-4 flex items-center justify-center relative cursor-pointer bg-white drop-shadow-lg`}
              onClick={async (e) => { e.stopPropagation(); fontModal(k as any); }}
            >
              <div 
                className="absolute top-0 right-0 w-14 h-14 bg-orange-400 flex items-center justify-center rounded-full translate-x-1/2 -translate-y-1/2 cursor-pointer"
                onClick={async (e) => { e.stopPropagation(); fontModal(k as any); }}
              >
                <div className="p-3 text-black">{Icons.pencil}</div>
              </div>

            <div className="text-center">
              <div className={`font-${k}-demo text-black text-2xl`}>{demoFonts[k as keyof typeof demoFonts].src || demoFonts[k as keyof typeof demoFonts].family}</div>
              <div className={`font-${k}-demo text-black text-2xl`}>{k.charAt(0).toUpperCase() + k.slice(1)}</div>
              <div className={`font-${k}-demo text-black text-md`}>{k.charAt(0).toUpperCase() + k.slice(1)}</div>
              <div className={`font-${k}-demo text-black text-xs`}>{k.charAt(0).toUpperCase() + k.slice(1)}</div>
            </div>

            
            </div>
          ))
        }
      </ColumnSnapper>













        <div className=" bg-white flex mt-3 my-4 mx-8 px-8">
          {
            (touched && !!formErrors.length) &&
              <select 
                className="inline-block alert alert-error mr-2 h-12 pt-4 text-sm">
                { 
                  formErrors.map(err => (<option className="text-xs">{err.message}</option>))
                }
              </select>
          }
          <button className={`btn btn-primary mr-2 ml-auto`} disabled={!touched || !!Object.keys(formErrors).length} onClick={() => {  
            (async () => {
              // console.log('submitting theme update', model);
              const res = await HttpService.post('themecreate', model);
              
              if (res.success) {
                modalContext.toast?.('success', 'Theme updated!');
                setStorageContext?.(sc => ({ ...sc, theme: model }));
              } else {
                modalContext.toast?.('error', 'Failed to update theme');
                console.log(res.messages);
              }
            })();
          }}>Save</button>
          <button className="btn btn-warning mr-0" onClick={() => {  
            (async () => {
              const res = await HttpService.delete('themedelete');
              if (res.success && res.body) {
                setStorageContext?.(sc => ({ ...sc, theme: res.body }));
                setModel((({ id, ...rest }) => rest)(res.body as Theme));
                modalContext.toast?.('success', 'Theme reverted to previous configuration');
              } else {
                modalContext.toast?.('error', 'Failed to revert theme to previous configuration.');
                console.log(res.messages);
              }
            })()
          }}>Revert</button>
        </div>

      </div>

    </div>
  )
}

export default AdminThemeForm;